/**
 * FASE 4.2 — AuthModalsHost
 * Manager globale dei modali Login/Register + drawer area cliente.
 * Espone un context per aprirli da QUALUNQUE componente del sito pubblico.
 *
 * Usage:
 *   const { openLogin, openRegister, openAreaCliente } = useAuthModals();
 *   openLogin();
 *   openAreaCliente("polizze"); // apre il drawer "Le mie polizze"
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import LoginModal from "@/components/auth/LoginModal";
import RegisterModal from "@/components/auth/RegisterModal";
import AreaClienteDrawer from "@/components/auth/AreaClienteDrawer";

const Ctx = createContext(null);
export const useAuthModals = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuthModals must be used inside <AuthModalsHost>");
  return c;
};

/**
 * Sezioni del drawer area cliente.
 * key = identificatore stabile usato in URL ?drawer=...
 */
export const DRAWER_SECTIONS = [
  "polizze", "preventivi", "documenti", "profilo",
  "notifiche", "impostazioni", "dashboard",
  "familiari",  // FASE K3 UX
];

export default function AuthModalsHost({ children }) {
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [drawerSection, setDrawerSection] = useState(null);
  const location = useLocation();

  const openLogin = useCallback(() => { setRegisterOpen(false); setLoginOpen(true); }, []);
  const openRegister = useCallback(() => { setLoginOpen(false); setRegisterOpen(true); }, []);
  const openAreaCliente = useCallback((section = "dashboard") => {
    setDrawerSection(DRAWER_SECTIONS.includes(section) ? section : "dashboard");
  }, []);
  const closeAll = useCallback(() => {
    setLoginOpen(false); setRegisterOpen(false); setDrawerSection(null);
  }, []);

  // Apertura via querystring ?login=1 oppure ?drawer=polizze (redirect /area-cliente/*)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("login") === "1" || sp.get("login") === "true") {
      setLoginOpen(true);
      // pulisce param da URL senza navigare
      sp.delete("login");
      const qs = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    const sec = sp.get("drawer");
    if (sec && DRAWER_SECTIONS.includes(sec)) {
      setDrawerSection(sec);
      sp.delete("drawer");
      const qs = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [location.search]);

  return (
    <Ctx.Provider value={{ openLogin, openRegister, openAreaCliente, closeAll }}>
      {children}
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)}
        onSwitchToRegister={openRegister} />
      <RegisterModal open={registerOpen} onClose={() => setRegisterOpen(false)}
        onSwitchToLogin={openLogin} />
      <AreaClienteDrawer section={drawerSection} onClose={() => setDrawerSection(null)} />
    </Ctx.Provider>
  );
}
